/**
 * The bottom-right corner belongs to the floating help button.
 *
 * It is `position: fixed; bottom: 24px; right: 24px`, 56 by 56, at
 * `theme.zIndex.fixed` — 1030. Anything else placed at the same coordinates is
 * COVERED by it, silently, and cannot be clicked at all.
 *
 * That is not hypothetical. On /players/:id the Follow button sat at exactly
 * `bottom: 24px; right: 24px` with `z-index: 100`, and on a 1440×900 screen the help
 * bubble sat squarely on top of it. A crawl reported it as unclickable, retried at 15
 * seconds, and it failed again — because it genuinely was.
 *
 * The failure is quiet in the worst way: both elements render, both look right in a
 * screenshot, and the covered one simply never responds.
 *
 * `HELP_BUTTON_CLEARANCE` is how far from the right edge everything else must start:
 * the button's 24px offset, its 56px width, and 16px of gap.
 */
export const HELP_BUTTON = {
  bottom: 24,
  right: 24,
  size: 56,
  gap: 16,
} as const;

/** Distance from the right edge that clears the help button, in px. */
export const HELP_BUTTON_CLEARANCE = HELP_BUTTON.right + HELP_BUTTON.size + HELP_BUTTON.gap;

/**
 * Position a fixed element in the bottom-right without landing under the help button.
 *
 * Use this for anything `position: fixed` in that corner rather than writing `right:
 * '96px'` by hand — the number moves if the button's size ever does, and a hand-written
 * copy will not.
 */
export const bottomRightClearOfHelp = (bottom = HELP_BUTTON.bottom) =>
  ({
    position: 'fixed',
    bottom: `${bottom}px`,
    right: `${HELP_BUTTON_CLEARANCE}px`,
  }) as const;
